import striptags from 'striptags'
import { textUrlRegexExtended } from '@awork/_shared/static-data/regular-expressions'
import { unescape } from '@awork/_shared/functions/lodash'

/**
 * Strips the html and replaces line breaks with default (\n) line breaks
 * @param {string} str
 * @returns {string}
 */

export function stripHTML(str: string): string {
  return striptags(str)
}

/**
 * Removes structured mention elements and returns normalized plain text.
 * @param {string} str Rich-text content that may contain mentions.
 * @returns {string} Plain text without entity or user mentions.
 */
export function stripMentionsFromText(str: string): string {
  const mentionElementRegex =
    /<span\b(?=[^>]*\bclass\s*=\s*(['"])[^'"]*\b(?:entity-mention|mention)\b[^'"]*\1)[^>]*>.*?<\/span\s*>/gis
  return stripHTML(str.replace(mentionElementRegex, ' ')).replace(/\s+/g, ' ').trim()
}

/**
 * Trims and removes empty lines at start and end of the rich text string
 * @param {string} str
 * @returns {string}
 */
export function trimRichText(str: string): string {
  if (str) {
    const emptyLineRegex = /^(<p><br><\/p>)+|(<p><br><\/p>)+$/g

    str = str.trim()
    str = str.replace(emptyLineRegex, '')
  }

  return str
}

/**
 * Removes plain and HTML whitespaces, including empty HTML tags (or containing
 * only <br> or &nbsp;)
 * @param {string} str
 * @returns {string}
 */
export function trimWhitespaces(str: string): string {
  const emptyBoundaryTags = /^(?:<(?!br)\w+>(?:<br>|&nbsp;)*<\/\w+>)+|(?:<(?!br)\w+>(?:<br>|&nbsp;)*<\/\w+>)+$/g

  return str?.replace(emptyBoundaryTags, '').trim()
}

/**
 * Unescapes any encoded html at the end of urls
 * @param {string} str
 * @returns {string}
 */
export function unescapeUrls(str: string): string {
  const urls = str.match(textUrlRegexExtended)

  urls?.forEach(url => {
    str = str.replace(url, unescape(url))
  })

  return str
}

/**
 * Unescapes HTML entities, converting characters such as '&amp;' to '&'.
 * @param {string} str
 * @returns {string}
 */
export function unescapeHtml(str: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(str, 'text/html')

  return doc.documentElement.textContent
}

/**
 * Escapes HTML-significant characters for safe use in text and attribute values.
 * @param {string} str The text to escape.
 * @returns {string} HTML-safe text.
 */
export function escapeHtml(str: string): string {
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}

/**
 * Get string size in bytes that can be used in validations.
 * @param string
 * @returns {number}
 */
export function getStringByteSize(string): number {
  if (!string) {
    return 0
  }

  return new Blob([string]).size
}

/**
 * Rotates a string using the ROT47 approach
 * (by 47 characters in the visible part of the ASCII table).
 * @param {string} input
 * @returns {string}
 */
export function rotateCharacters(input: string): string {
  const rot47Distance = 47
  const asciiVisibleStart = 33
  const asciiVisibleEnd = 126

  let result = ''

  for (let i = 0; i < input.length; i++) {
    const charCode = input.charCodeAt(i)

    if (charCode >= asciiVisibleStart && charCode <= asciiVisibleEnd) {
      const rotatedCharCode =
        ((charCode - asciiVisibleStart + rot47Distance) % (asciiVisibleEnd - asciiVisibleStart + 1)) + asciiVisibleStart

      result += String.fromCharCode(rotatedCharCode)
    } else {
      result += input.charAt(i)
    }
  }

  return result
}

/**
 * Gets Email domain name
 * @param {string} email
 * @returns {string}
 */
export function getEmailDomainName(email: string): string {
  return email.split('@')[1]
}

/**
 * Encodes a UTF-8 string to base64.
 * @param {string} str The string to encode
 * @returns {string} Base64-encoded string
 */
export function encodeUtf8ToBase64(str: string): string {
  const bytes = new TextEncoder().encode(str)
  const binaryString = String.fromCharCode(...bytes)

  return btoa(binaryString)
}

/**
 * Decodes a base64-encoded string to UTF-8.
 * This function properly handles UTF-8 characters including umlauts (ä, ö, ü) and other special characters.
 * @param {string} base64 The base64-encoded string
 * @returns {string} The decoded UTF-8 string
 */
export function base64ToUtf8(base64: string): string {
  const binaryString = atob(base64)
  const bytes = Uint8Array.from(binaryString, m => m.codePointAt(0)!)

  return new TextDecoder().decode(bytes)
}
